import { useState, useRef, useEffect } from 'react'
import { MessageCircle, Send, MoreHorizontal, Reply, Heart, Flag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'
import { zhCN } from 'date-fns/locale'

interface Comment {
  id: string
  content: string
  author: {
    id: string
    name: string
    avatar?: string
    level: number
  }
  createdAt: Date
  likes: number
  liked: boolean
  replies?: Comment[]
  replyTo?: {
    id: string
    name: string
  }
}

interface CommentSystemProps {
  postId: string
  comments: Comment[]
  onAddComment: (content: string, replyTo?: string) => void
  onLikeComment: (commentId: string) => void
  onReportComment: (commentId: string) => void
  // 展开评论时触发（用于从后端加载评论列表）
  onOpenComments?: () => void
  className?: string
}

export function CommentSystem({ 
  postId, 
  comments, 
  onAddComment, 
  onLikeComment, 
  onReportComment,
  onOpenComments,
  className 
}: CommentSystemProps) {
  const [newComment, setNewComment] = useState('')
  const [replyTo, setReplyTo] = useState<{ id: string, name: string } | null>(null)
  const [showComments, setShowComments] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const toggleComments = () => {
    const next = !showComments
    setShowComments(next)
    if (next) {
      onOpenComments?.()
    }
  }

  const handleSubmit = () => {
    if (!newComment.trim()) return
    
    onAddComment(newComment, replyTo?.id)
    setNewComment('')
    setReplyTo(null)
  }

  const handleReply = (comment: Comment) => {
    setReplyTo({ id: comment.id, name: comment.author?.name || '匿名用户' })
    textareaRef.current?.focus()
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* 评论按钮和数量 */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleComments}
        className="gap-2 text-gray-600 hover:text-blue-500 hover:bg-blue-50"
      >
        <MessageCircle className="w-4 h-4" />
        <span>{comments.length > 0 ? `${comments.length} 条评论` : '评论'}</span>
      </Button>

      {showComments && (
        <div className="space-y-4 border-t pt-4">
          {/* 评论输入框 */}
          <div className="space-y-2">
            {replyTo && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                <Reply className="w-3 h-3" />
                <span>回复 @{replyTo.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReplyTo(null)}
                  className="ml-auto h-6 w-6 p-0"
                >
                  ×
                </Button>
              </div>
            )}
            
            <div className="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={replyTo ? `回复 @${replyTo.name}...` : "写下你的评论..."}
                className="min-h-[80px] resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    handleSubmit()
                  }
                }}
              />
              <Button
                onClick={handleSubmit}
                disabled={!newComment.trim()}
                className="self-end"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="text-xs text-gray-500">
              按 Ctrl+Enter 快速发送
            </div>
          </div>

          {/* 评论列表 */}
          <div className="space-y-4">
            {comments.length > 0 ? comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onLike={onLikeComment}
                onReply={handleReply}
                onReport={onReportComment}
              />
            )) : (
              <p className="text-sm text-gray-400 text-center py-4">还没有评论，来抢沙发吧~</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function CommentItem({ 
  comment, 
  onLike, 
  onReply, 
  onReport,
  isReply = false 
}: { 
  comment: Comment
  onLike: (commentId: string) => void
  onReply: (comment: Comment) => void
  onReport: (commentId: string) => void
  isReply?: boolean
}) {
  return (
    <div className={cn("space-y-2", isReply && "ml-8 border-l-2 border-gray-100 pl-4")}>
      <div className="flex gap-3">
        <Avatar className="w-8 h-8">
          <AvatarImage src={comment.author?.avatar} />
          <AvatarFallback>{comment.author?.name?.[0] || '?'}</AvatarFallback>
        </Avatar>
        
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{comment.author?.name || '匿名用户'}</span>
            <Badge 
              variant="secondary" 
              className={cn("text-xs text-white", getLevelColor(comment.author?.level || 1))}
            >
              Lv.{comment.author?.level || 1}
            </Badge>
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(comment.createdAt, { addSuffix: true, locale: zhCN })}
            </span>
          </div>
          
          <div className="text-gray-800">
            {comment.replyTo && (
              <span className="text-blue-500 mr-1">@{comment.replyTo.name}</span>
            )}
            {comment.content}
          </div>
          
          <div className="flex items-center gap-4 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onLike(comment.id)}
              className={cn(
                "h-7 px-2 gap-1 text-xs",
                comment.liked 
                  ? "text-red-500 hover:text-red-600" 
                  : "text-gray-500 hover:text-red-500"
              )}
            >
              <Heart className={cn("w-3 h-3", comment.liked && "fill-current")} />
              {comment.likes > 0 && comment.likes}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReply(comment)}
              className="h-7 px-2 gap-1 text-xs text-gray-500 hover:text-blue-500"
            >
              <Reply className="w-3 h-3" />
              回复
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600"
                >
                  <MoreHorizontal className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onReport(comment.id)} className="text-red-600">
                  <Flag className="w-4 h-4 mr-2" />
                  举报
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* 回复列表 */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-2">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onLike={onLike}
              onReply={onReply}
              onReport={onReport}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function getLevelColor(level: number) {
  if (level >= 10) return 'bg-purple-500'
  if (level >= 5) return 'bg-blue-500'
  if (level >= 3) return 'bg-green-500'
  return 'bg-gray-500'
}